import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { 
  ArrowLeft,
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Trophy,
  Clock,
  CheckCircle2,
  Circle,
  Edit,
  Star,
  TrendingUp,
  Package,
  Trash2,
  ArrowRightLeft,
  Gift,
  Target,
  FileText,
  CreditCard,
  UserCircle
} from 'lucide-react';
import { format } from 'date-fns';

const StudentDetailView = ({ studentId, studentData, onBack, onEdit }) => {
  const [activeTab, setActiveTab] = useState('personal');
  const [student, setStudent] = useState(studentData || null);
  const [progress, setProgress] = useState(null);
  const [attendance, setAttendance] = useState([]);
  const [stars, setStars] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isProgressDialogOpen, setIsProgressDialogOpen] = useState(false);
  const [isKitDialogOpen, setIsKitDialogOpen] = useState(false);
  const [isTransferDialogOpen, setIsTransferDialogOpen] = useState(false);
  const [centers, setCenters] = useState([]);
  const [kitNotes, setKitNotes] = useState('');
  const [transferCenterId, setTransferCenterId] = useState('');
  const [transferReason, setTransferReason] = useState('');
  const [transferConfirmation, setTransferConfirmation] = useState('');
  const [formData, setFormData] = useState({
    layer_one_complete: false,
    layer_two_complete: false,
    cube_complete: false,
    best_time_seconds: '',
    notes: ''
  });
  
  const user = JSON.parse(localStorage.getItem('user'));
  const isTeacher = user?.role === 'teacher';
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    loadStudentData();
    if (isAdmin) {
      loadCenters();
    }
  }, [studentId]);

  const loadStudentData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [studentRes, progressRes, attendanceRes, starsRes, invoicesRes] = await Promise.all([
        student ? Promise.resolve({ data: student }) : axios.get(`${API}/users/${studentId}`, { headers }),
        axios.get(`${API}/student-progress/${studentId}`, { headers }),
        axios.get(`${API}/attendance/student/${studentId}`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/stars/student/${studentId}`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/invoices/student/${studentId}`, { headers }).catch(() => ({ data: [] }))
      ]);

      setStudent(studentRes.data);
      setProgress(progressRes.data[0] || null);
      setAttendance(attendanceRes.data);
      setStars(starsRes.data);
      setInvoices(invoicesRes.data);
    } catch (error) {
      console.error('Error loading student data:', error);
      toast.error('Failed to load student details');
    } finally {
      setLoading(false);
    }
  };

  const loadCenters = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/centers`, { headers: { Authorization: `Bearer ${token}` } });
      setCenters(res.data);
    } catch (error) {
      console.error('Error loading centers:', error);
    }
  };

  const openProgressDialog = () => {
    if (progress) {
      setFormData({
        layer_one_complete: progress.milestones?.layer_one_complete || false,
        layer_two_complete: progress.milestones?.layer_two_complete || false,
        cube_complete: progress.milestones?.cube_complete || false,
        best_time_seconds: progress.best_time_seconds || '',
        notes: progress.notes || ''
      });
    } else {
      setFormData({
        layer_one_complete: false,
        layer_two_complete: false,
        cube_complete: false,
        best_time_seconds: '',
        notes: ''
      });
    }
    setIsProgressDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/student-progress?student_id=${studentId}`,
        {
          course_name: 'Course 1',
          ...formData,
          best_time_seconds: formData.best_time_seconds ? parseInt(formData.best_time_seconds) : null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Progress updated successfully');
      setIsProgressDialogOpen(false);
      loadStudentData();
    } catch (error) {
      console.error('Error updating progress:', error);
      toast.error('Failed to update progress');
    }
  };

  const handleKitRequest = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/kit-requests`,
        {
          student_id: studentId,
          student_name: student.name,
          requested_by: user.id,
          notes: kitNotes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Kit request submitted successfully');
      setIsKitDialogOpen(false);
      setKitNotes('');
    } catch (error) {
      console.error('Error submitting kit request:', error);
      toast.error('Failed to submit kit request');
    }
  };

  const handleTransfer = async () => {
    if (transferConfirmation !== student.name) {
      toast.error('Please type the student name to confirm transfer');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/users/transfer/${studentId}`,
        {
          new_center_id: transferCenterId,
          reason: transferReason
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Student transferred successfully');
      setIsTransferDialogOpen(false);
      onBack();
    } catch (error) {
      console.error('Error transferring student:', error);
      toast.error('Failed to transfer student');
    }
  };

  const handleDelete = async () => {
    if (window.confirm(`Are you sure you want to delete ${student.name}? This action cannot be undone.`)) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${API}/users/${studentId}`, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Student deleted successfully');
        onBack();
      } catch (error) {
        console.error('Error deleting student:', error);
        toast.error('Failed to delete student');
      }
    }
  };

  const formatTime = (seconds) => {
    if (!seconds) return 'Not set';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const getProgressPercentage = () => {
    if (!progress) return 0;
    const milestones = progress.milestones || {};
    const completed = [
      milestones.layer_one_complete,
      milestones.layer_two_complete,
      milestones.cube_complete
    ].filter(Boolean).length;
    return Math.round((completed / 3) * 100);
  };

  const calculateAttendanceRate = () => {
    if (!attendance.length) return 0;
    const present = attendance.filter(a => a.status === 'present').length;
    return Math.round((present / attendance.length) * 100);
  };

  const calculateAverageStars = () => {
    if (!stars.length) return 0;
    const total = stars.reduce((sum, s) => sum + s.stars, 0);
    return (total / stars.length).toFixed(1);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  if (!student) {
    return <div className="text-center py-8">Student not found</div>;
  }

  const progressPercentage = getProgressPercentage();
  const milestones = progress?.milestones || {};

  const tabs = [
    { id: 'personal', label: 'Personal Information', icon: UserCircle },
    { id: 'progress', label: 'Progress', icon: Target },
    { id: 'attendance', label: 'Attendance', icon: Calendar },
    { id: 'payment', label: 'Payment History', icon: CreditCard }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={onBack} className="flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" />
          Back to Students
        </Button>
        
        {(isTeacher || isAdmin) && (
          <div className="flex items-center gap-2">
            <Button onClick={() => onEdit(student)} variant="outline" className="flex items-center gap-2">
              <Edit className="w-4 h-4" />
              Edit
            </Button>
            <Button onClick={openProgressDialog} className="bg-blue-600 hover:bg-blue-700 flex items-center gap-2">
              <Target className="w-4 h-4" />
              Update Progress
            </Button>
            <Button onClick={() => setIsKitDialogOpen(true)} className="bg-green-600 hover:bg-green-700 flex items-center gap-2">
              <Package className="w-4 h-4" />
              Order Kit
            </Button>
            <Button onClick={() => setIsTransferDialogOpen(true)} variant="outline" className="text-orange-600 border-orange-300 hover:bg-orange-50 flex items-center gap-2">
              <ArrowRightLeft className="w-4 h-4" />
              Transfer
            </Button>
            <Button onClick={handleDelete} variant="outline" className="text-red-600 border-red-300 hover:bg-red-50 flex items-center gap-2">
              <Trash2 className="w-4 h-4" />
              Delete
            </Button>
          </div>
        )}
      </div>

      {/* Student Header Card */}
      <Card className="p-6">
        <div className="flex items-start gap-6">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center flex-shrink-0">
            <User className="w-10 h-10 text-white" />
          </div>
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900 mb-1">{student.name}</h1>
            <p className="text-gray-600">{student.email}</p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
              student.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
            }`}>
              {student.status}
            </span>
            {student.gk_code && (
              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">
                {student.gk_code}
              </span>
            )}
          </div>
        </div>
      </Card>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Course Progress</p>
              <p className="text-2xl font-bold text-blue-600">{progressPercentage}%</p>
            </div>
            <TrendingUp className="w-8 h-8 text-blue-500" />
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Attendance Rate</p>
              <p className="text-2xl font-bold text-green-600">{calculateAttendanceRate()}%</p>
            </div>
            <Calendar className="w-8 h-8 text-green-500" />
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Average Stars</p>
              <p className="text-2xl font-bold text-yellow-600">{calculateAverageStars()}</p>
            </div>
            <Star className="w-8 h-8 text-yellow-500 fill-current" />
          </div>
        </Card>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex gap-1">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-6 py-3 font-medium border-b-2 transition-all ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
              }`}
            >
              <tab.icon className="w-5 h-5" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {/* Personal Information Tab */}
        {activeTab === 'personal' && (
          <Card className="p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Personal Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-sm font-medium text-gray-600">Full Name</label>
                <p className="text-base text-gray-900 mt-1">{student.name || 'Not provided'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Email</label>
                <p className="text-base text-gray-900 mt-1">{student.email || 'Not provided'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Contact Number</label>
                <p className="text-base text-gray-900 mt-1">{student.contact_number || 'Not provided'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Alternate Contact</label>
                <p className="text-base text-gray-900 mt-1">{student.alternate_contact_number || 'Not provided'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Date of Birth</label>
                <p className="text-base text-gray-900 mt-1">
                  {student.date_of_birth ? format(new Date(student.date_of_birth), 'MMM dd, yyyy') : 'Not provided'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Gender</label>
                <p className="text-base text-gray-900 mt-1">{student.gender || 'Not provided'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Father's Name</label>
                <p className="text-base text-gray-900 mt-1">{student.father_name || 'Not provided'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Mother's Name</label>
                <p className="text-base text-gray-900 mt-1">{student.mother_name || 'Not provided'}</p>
              </div>
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-gray-600">Address</label>
                <p className="text-base text-gray-900 mt-1">{student.address || 'Not provided'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">School Name</label>
                <p className="text-base text-gray-900 mt-1">{student.school_name || 'Not provided'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Grade</label>
                <p className="text-base text-gray-900 mt-1">{student.grade || 'Not provided'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Student Mode</label>
                <p className="text-base text-gray-900 mt-1">{student.student_mode || 'Not provided'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Payment Term</label>
                <p className="text-base text-gray-900 mt-1">{student.payment_term || 'Not provided'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Date of Joining</label>
                <p className="text-base text-gray-900 mt-1">
                  {student.date_of_joining ? format(new Date(student.date_of_joining), 'MMM dd, yyyy') : 'Not provided'}
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Progress Tab */}
        {activeTab === 'progress' && (
          <div className="space-y-6">
            <Card className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Course 1 - Milestones</h2>
              
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Overall Progress</span>
                  <span className="text-sm font-bold text-blue-600">{progressPercentage}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div className="bg-blue-600 h-3 rounded-full transition-all" style={{ width: `${progressPercentage}%` }} />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className={`p-4 rounded-lg border-2 ${
                  milestones.layer_one_complete ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-200'
                }`}>
                  <div className="flex items-center gap-3 mb-2">
                    {milestones.layer_one_complete ? (
                      <CheckCircle2 className="w-6 h-6 text-green-600" />
                    ) : (
                      <Circle className="w-6 h-6 text-gray-400" />
                    )}
                    <h3 className="font-semibold text-gray-900">Layer One</h3>
                  </div>
                  {milestones.layer_one_completed_at && (
                    <p className="text-xs text-gray-600">
                      {format(new Date(milestones.layer_one_completed_at), 'MMM dd, yyyy')}
                    </p>
                  )}
                </div>

                <div className={`p-4 rounded-lg border-2 ${
                  milestones.layer_two_complete ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-200'
                }`}>
                  <div className="flex items-center gap-3 mb-2">
                    {milestones.layer_two_complete ? (
                      <CheckCircle2 className="w-6 h-6 text-green-600" />
                    ) : (
                      <Circle className="w-6 h-6 text-gray-400" />
                    )}
                    <h3 className="font-semibold text-gray-900">Layer Two</h3>
                  </div>
                  {milestones.layer_two_completed_at && (
                    <p className="text-xs text-gray-600">
                      {format(new Date(milestones.layer_two_completed_at), 'MMM dd, yyyy')}
                    </p>
                  )}
                </div>

                <div className={`p-4 rounded-lg border-2 ${
                  milestones.cube_complete ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-200'
                }`}>
                  <div className="flex items-center gap-3 mb-2">
                    {milestones.cube_complete ? (
                      <CheckCircle2 className="w-6 h-6 text-green-600" />
                    ) : (
                      <Circle className="w-6 h-6 text-gray-400" />
                    )}
                    <h3 className="font-semibold text-gray-900">Cube Complete</h3>
                  </div>
                  {milestones.cube_completed_at && (
                    <p className="text-xs text-gray-600">
                      {format(new Date(milestones.cube_completed_at), 'MMM dd, yyyy')}
                    </p>
                  )}
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Trophy className="w-10 h-10 text-yellow-600" />
                    <div>
                      <p className="text-sm text-gray-600">Best Time</p>
                      <p className="text-3xl font-bold text-yellow-700">{formatTime(progress?.best_time_seconds)}</p>
                    </div>
                  </div>
                  {progress?.best_time_recorded_at && (
                    <p className="text-xs text-gray-600">
                      {format(new Date(progress.best_time_recorded_at), 'MMM dd, yyyy')}
                    </p>
                  )}
                </div>
              </div>

              {progress?.notes && (
                <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm font-semibold text-gray-900 mb-1">Teacher's Notes</p>
                  <p className="text-sm text-gray-700">{progress.notes}</p>
                  {progress.updated_by_name && (
                    <p className="text-xs text-gray-500 mt-2">
                      By {progress.updated_by_name} • {format(new Date(progress.updated_at), 'MMM dd, yyyy')}
                    </p>
                  )}
                </div>
              )}
            </Card>

            {/* Star Ratings */}
            <Card className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Star Ratings</h2>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {stars.map((record, idx) => (
                  <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className={`w-4 h-4 ${
                            i < record.stars ? 'text-yellow-400 fill-current' : 'text-gray-300'
                          }`} />
                        ))}
                      </div>
                      <span className="text-sm font-bold text-yellow-600">{record.stars}/5</span>
                    </div>
                    {record.comment && (
                      <p className="text-xs text-gray-600">{record.comment}</p>
                    )}
                  </div>
                ))}
                {stars.length === 0 && (
                  <p className="text-gray-500 text-center py-8">No ratings yet</p>
                )}
              </div>
            </Card>
          </div>
        )}

        {/* Attendance Tab */}
        {activeTab === 'attendance' && (
          <Card className="p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Attendance Records</h2>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {attendance.map((record, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm text-gray-700">
                    {format(new Date(record.date), 'EEEE, MMMM dd, yyyy')}
                  </span>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    record.status === 'present' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {record.status}
                  </span>
                </div>
              ))}
              {attendance.length === 0 && (
                <p className="text-gray-500 text-center py-8">No attendance records yet</p>
              )}
            </div>
          </Card>
        )}

        {/* Payment History Tab */}
        {activeTab === 'payment' && (
          <Card className="p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Payment History</h2>
            <div className="space-y-3">
              {invoices.map((invoice, idx) => (
                <div key={idx} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="font-semibold text-gray-900">{invoice.invoice_type}</p>
                      <p className="text-sm text-gray-600">
                        {format(new Date(invoice.invoice_date), 'MMM dd, yyyy')}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-gray-900">₹{invoice.amount}</p>
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${
                        invoice.status === 'paid' ? 'bg-green-100 text-green-700' : 
                        invoice.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 
                        'bg-red-100 text-red-700'
                      }`}>
                        {invoice.status}
                      </span>
                    </div>
                  </div>
                  {invoice.payment_date && (
                    <p className="text-xs text-gray-600">
                      Paid on {format(new Date(invoice.payment_date), 'MMM dd, yyyy')}
                    </p>
                  )}
                </div>
              ))}
              {invoices.length === 0 && (
                <p className="text-gray-500 text-center py-8">No payment records yet</p>
              )}
            </div>
          </Card>
        )}
      </div>

      {/* Progress Dialog */}
      <Dialog open={isProgressDialogOpen} onOpenChange={setIsProgressDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Update Progress - {student.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center space-x-3 p-3 border rounded-lg">
                <Checkbox
                  id="layer_one"
                  checked={formData.layer_one_complete}
                  onCheckedChange={(checked) => setFormData({ ...formData, layer_one_complete: checked })}
                />
                <Label htmlFor="layer_one" className="cursor-pointer flex-1">
                  <div className="font-medium">Layer One Complete</div>
                </Label>
              </div>
              <div className="flex items-center space-x-3 p-3 border rounded-lg">
                <Checkbox
                  id="layer_two"
                  checked={formData.layer_two_complete}
                  onCheckedChange={(checked) => setFormData({ ...formData, layer_two_complete: checked })}
                />
                <Label htmlFor="layer_two" className="cursor-pointer flex-1">
                  <div className="font-medium">Layer Two Complete</div>
                </Label>
              </div>
              <div className="flex items-center space-x-3 p-3 border rounded-lg">
                <Checkbox
                  id="cube_complete"
                  checked={formData.cube_complete}
                  onCheckedChange={(checked) => setFormData({ ...formData, cube_complete: checked })}
                />
                <Label htmlFor="cube_complete" className="cursor-pointer flex-1">
                  <div className="font-medium">Cube Complete</div>
                </Label>
              </div>
            </div>
            <div>
              <Label htmlFor="best_time">Best Time (seconds)</Label>
              <Input
                id="best_time"
                type="number"
                placeholder="e.g., 120"
                value={formData.best_time_seconds}
                onChange={(e) => setFormData({ ...formData, best_time_seconds: e.target.value })}
                min="1"
              />
            </div>
            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                placeholder="Add notes..."
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
              />
            </div>
            <div className="flex gap-3">
              <Button type="button" variant="outline" onClick={() => setIsProgressDialogOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-blue-600">
                Save Progress
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Kit Dialog */}
      <Dialog open={isKitDialogOpen} onOpenChange={setIsKitDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Order Kit - {student.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="kit_notes">Notes</Label>
              <Textarea
                id="kit_notes"
                placeholder="Special instructions..."
                value={kitNotes}
                onChange={(e) => setKitNotes(e.target.value)}
                rows={3}
              />
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setIsKitDialogOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleKitRequest} className="flex-1 bg-green-600">
                Submit Request
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Transfer Dialog */}
      <Dialog open={isTransferDialogOpen} onOpenChange={setIsTransferDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Transfer Student</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">⚠️ This will transfer {student.name} to a different center.</p>
            </div>
            <div>
              <Label htmlFor="transfer_center">New Center</Label>
              <Select value={transferCenterId} onValueChange={setTransferCenterId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose center" />
                </SelectTrigger>
                <SelectContent>
                  {centers.map(center => (
                    <SelectItem key={center.id} value={center.id}>{center.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="transfer_reason">Reason</Label>
              <Textarea
                id="transfer_reason"
                value={transferReason}
                onChange={(e) => setTransferReason(e.target.value)}
                rows={2}
              />
            </div>
            <div>
              <Label htmlFor="transfer_confirmation">Type student name to confirm: <span className="font-bold">{student.name}</span></Label>
              <Input
                id="transfer_confirmation"
                value={transferConfirmation}
                onChange={(e) => setTransferConfirmation(e.target.value)}
              />
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setIsTransferDialogOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button 
                onClick={handleTransfer} 
                disabled={transferConfirmation !== student.name || !transferCenterId}
                className="flex-1 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Confirm Transfer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StudentDetailView;
